import {
  onCleanup,
  createEffect,
  createRoot,
  on,
  createSignal,
  createMemo,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { register, compose } from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

const operators = {
  in: (condition, value) => condition.value.includes(condition.fieldName in value),
  includes: (condition, value, selectedValues) => {
    const result = condition.value.includes(
      selectedValues[condition.valueName] === null ||
        value[condition.fieldName].includes(selectedValues[condition.valueName])
    );
    return result;
  },
};

function FilterSlides(props, { element }) {
  const [slider, setSlider] = createSignal(null);
  const originalSlides = createMemo(() => {
    if (!slider()) return [];
    return Array.from(slider().slides);
  });

  const filteredSlides = createMemo(() => {
    return originalSlides().filter((slide, i) => {
      const item = JSON.parse(slide.getAttribute('data-filterable'));
      return props.filters.reduce((result, filter) => {
        return (
          filter.conditions.reduce((result, condition) => {
            const outcome = operators[condition.operator](condition, item, props.values);
            return result && outcome;
          }, true) && result
        );
      }, true);
    });
  });

  createEffect(
    on(filteredSlides, (slides) => {
      const s = slider();
      if (!s) return;
      const track = s.el.querySelector('.blaze-track');
      track.replaceChildren(...slides);
      s.refresh();
    })
  );

  createEffect(
    on(
      () => props.target,
      (target) => {
        if (!target) return;
        function updateSlider(e) {
          setSlider(e.detail.slider);
        }

        element.addEventListener(`blaze-slider.loaded.${target}`, updateSlider);

        return onCleanup(() => {
          element.removeEventListener(`blaze-slider.loaded.${target}`, updateSlider);
        });
      }
    )
  );

  createEffect(
    on(
      () => props.id,
      (id) => {
        if (!id) return;

        function updateValue(e) {
          element.setAttribute(
            'values',
            JSON.stringify(
              {
                ...(props?.values || {}),
                [e.detail.name]: e.detail.value,
              },
              null,
              2
            )
          );
        }

        window.addEventListener(`filter-slides.${id}.event`, updateValue);

        return onCleanup(() => {
          window.removeEventListener(`filter-slides.${id}.event`, updateValue);
        });
      }
    )
  );
}

function FilterSlidesTrigger(props, { element }) {
  if (!props.on || !props.target) return;
  createEffect(
    on(
      () => ({ target: props.target, on: props.on }),
      ({ target, on }) => {
        if (!target || !on) return;

        function notifyTarget(e) {
          dispatchEvent(
            new CustomEvent(`filter-slides.${target}.event`, {
              detail: {
                value: e.target.value,
                name: e.target.name,
                type: e.target.type,
              },
            })
          );
        }

        element.addEventListener(on, notifyTarget);

        return onCleanup(() => {
          element.removeEventListener(on, notifyTarget);
        });
      }
    )
  );
}

customShadowlessElement('filter-slides', { values: {}, filters: [], id: '', target: '' }, FilterSlides);
customShadowlessElement('filter-slides-trigger', { target: '', on: '' }, FilterSlidesTrigger);
